import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button } from 'reactstrap';

import { useLanguageContext } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages.js';

import { FIELD_TYPES } from '../../constants';
import { buildFormSections } from '../../utils';
import FormSection from '../FormSection';

import ProfileFormField from './components/ProfileFormField';

const propTypes = {

	/**
	 * The name of the organization.
	 */
	orgName: PropTypes.string.isRequired,

	/**
	 * The profile form.
	 */
	form: PropTypes.shape({

		/**
		 * The form fields.
		 */
		form_fields: PropTypes.arrayOf(PropTypes.shape({

			id: PropTypes.string.isRequired,

		})).isRequired,

	}),

	/**
	 * The profile form entry data for the current user.
	 */
	data: PropTypes.shape({

		/**
		 * The profile form field -> profile form value.
		 */
		id: PropTypes.string,

	}),

	/**
	 * True, if the user is the editing the the profile form.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the user is viewing their own profile form.
	 */
	isSelf: PropTypes.bool,

	/**
	 * The error state related to editing the profile form.
	 */
	errors: PropTypes.shape({

		/**
		 * A mapping of form field ID -> true/false.
		 */
		id: PropTypes.string,

		/**
		 * The details related to an API error (if any).
		 */
		apiError: PropTypes.shape({}),

	}).isRequired,

	/**
	 * Callback to update the values for the profile form.
	 */
	onChange: PropTypes.func,

	/**
	 * Callback to save the updated values for the profile form.
	 */
	onSave: PropTypes.func,

	/**
	 * Callback to toggle on/off the editing state for the profile form.
	 */
	onToggleEdit: PropTypes.func,

};

/**
 * Component that shows the form data filled out by the user for their profile.
 *
 * @param {Object} props The component props.
 *
 * @returns {Object} The ProfileForm component.
 */
const ProfileForm = (props) => {

	const {
		orgName,
		form,
		data,
		isEditing,
		isSelf,
		errors,
		onChange,
		onSave,
		onToggleEdit,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Check if the profile form entry has errors (i.e. at least one)
	const hasErrors = form.form_fields?.reduce((acc, field) => {
		return acc || errors[field.id];
	}, false);

	// Parse the profile form to build the ordered list of form items, i.e. fields and sections
	const formSections = useMemo(() => buildFormSections(form), [form]);

	// If the user is not viewing their own profile form
	if (!isSelf) {

		formSections.forEach((section) => {

			section.fields = section.fields.filter((field) => {

				// Let's hide any empty fields
				if (data[field.id] === null ||
					data[field.id] === undefined ||
					data[field.id] === '') {
					return false;
				}

				// Let's hide social media fields too
				if (field.type === FIELD_TYPES.SOCIAL_MEDIA) {
					return false;
				}

				return true;

			});

		});

	}

	const formFields = formSections.reduce((fields, section) => (
		fields.concat(section.fields)
	), []);

	return (
		<div>
			{formFields.length > 0 && formSections.map((section, index) => {

				// Get the translated content for the form section
				const formSectionContent = chooseContentFromLanguage(section.form_section_contents);

				return (
					<FormSection
						key={section.id}
						section={{
							...section,
							title: formSectionContent?.title || section.title,
							description: formSectionContent?.description || section.description,
						}}
						index={index}>
						{section.fields.map((field) => (
							<ProfileFormField
								key={field.id}
								orgName={orgName}
								field={field}
								value={data[field.id]}
								privacy={data[`${field.id}:privacy`]}
								error={errors[`${field.id}_msg`]}
								isEditing={isEditing}
								isInvalid={errors[field.id]}
								onChange={onChange} />
						))}
					</FormSection>
				);

			})}
			{formFields.length === 0 &&
				<FormattedMessage {...messages.TC_COMPONENTS_FORM_USER_PROFILE_EMPTY_MSG_IS_OTHER} />
			}
			{isEditing &&
				<div>
					<Button
						color="primary"
						disabled={hasErrors}
						type="submit"
						onClick={onSave}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
					</Button>
					<Button
						className="ms-3"
						color="neutral"
						type="button"
						outline
						onClick={onToggleEdit}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
					</Button>
				</div>
			}
		</div>
	);

};

ProfileForm.propTypes = propTypes;

export default ProfileForm;
